import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchDepartures, type Departure } from '../lib/departures';
import {
  todayVenue,
  venueWeekStart,
  rangeDays,
  addDaysVenue,
  formatVenueLong,
  formatVenueShort,
  venueWeekLabel,
} from '../lib/dates';
import { formatError } from '../lib/errors';
import { fetchYtdGuests, CALENDAR_LIFECYCLE } from '../lib/analytics';
import { SlotCell, TeamSlotCell, type SlotCreatePrefill } from '../components/SlotCell';
import { fetchCalendarTeamOutings, type CalendarTeamSlot } from '../lib/teamQuotes';
import {
  breaksByCell,
  computeBreaks,
  fetchCalendarOverrides,
  fetchScheduleRules,
  notesByCell,
  overrideNoteLabel,
  type CalendarBreak,
  type CalendarOverrideRow,
  type OverrideNote,
  type ScheduleRuleRow,
} from '../lib/calendar-breaks';
import { DepartureModal } from '../components/DepartureModal';
import { NewBookingModal } from '../components/NewBookingModal';
import { useLocationFilter } from '../lib/location-context';
import { useHeaderSlot, type HeaderSlot } from '../lib/header-slot';

type Mode = 'week' | 'day';

const REFRESH_MS = 30_000;
const ONLY_BOOKED_KEY = 'neoseum-admin-cal-only-booked';

function parseHM(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

interface NewBookingPrefill {
  date?: string;
  time?: string;
  experienceId?: string;
  locationId?: string;
}

export function CalendarScreen() {
  const [mode, setMode] = useState<Mode>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches
      ? 'day'
      : 'week',
  );
  const [anchor, setAnchor] = useState<string>(() => todayVenue());
  const [departures, setDepartures] = useState<Departure[]>([]);
  // Team outings holding slots in view, by `${date}|${HH:MM}` (work item team-uitjes).
  const [teamSlots, setTeamSlots] = useState<CalendarTeamSlot[]>([]);
  // Opening rules (for pauses between periods) and slot overrides (for their
  // reason) in view; work item calendar-pauses-override-reasons.
  const [scheduleRules, setScheduleRules] = useState<ScheduleRuleRow[]>([]);
  const [overrideRows, setOverrideRows] = useState<CalendarOverrideRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Departure[] | null>(null);
  const [newBooking, setNewBooking] = useState<NewBookingPrefill | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [onlyBooked, setOnlyBooked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    // Default ON. Only honour an explicit '0' from a previous session;
    // missing key or '1' both keep the toggle enabled.
    return window.localStorage.getItem(ONLY_BOOKED_KEY) !== '0';
  });

  const days: string[] = useMemo(() => {
    if (mode === 'day') return [anchor];
    return rangeDays(venueWeekStart(anchor), 7);
  }, [mode, anchor]);

  const fromDate = days[0]!;
  const toDate = days[days.length - 1]!;
  const { locationId, setLocationId, locations } = useLocationFilter();

  // Tracks the most-recent failed refresh, separate from `error`. We only
  // surface a full-screen error card on the FIRST load (no data yet) — once
  // we've successfully loaded once, transient mobile-network blips ("Failed
  // to fetch") just show a small inline notice next to the refresh time and
  // get retried with a fast backoff (2s → 5s → 15s) before settling back
  // into the normal 30s tick.
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);
  const RETRY_DELAYS_MS = [2_000, 5_000, 15_000];

  const load = useCallback(async () => {
    try {
      const [rows, teams, rules, overs] = await Promise.all([
        fetchDepartures(fromDate, toDate, { locationId }),
        // Optional overlays: a failure here never blocks the calendar.
        fetchCalendarTeamOutings(fromDate, toDate, locationId).catch(() => null),
        fetchScheduleRules(locationId).catch(() => null),
        fetchCalendarOverrides(fromDate, toDate).catch(() => null),
      ]);
      setDepartures(rows);
      if (teams) setTeamSlots(teams);
      if (rules) setScheduleRules(rules);
      if (overs) setOverrideRows(overs);
      setLastRefresh(new Date());
      setError(null);
      setRefreshError(null);
      retryAttemptRef.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    } catch (e) {
      const msg = formatError(e);
      // First-load failure: show the prominent error card so the screen is
      // self-explanatory. Otherwise: keep the stale data visible, mark the
      // refresh as failed, and queue a fast retry. Capped at 3 attempts;
      // after that the normal 30s interval takes over.
      setDepartures((prev) => {
        if (prev.length === 0) setError(msg);
        else                   setRefreshError(msg);
        return prev;
      });
      if (retryAttemptRef.current < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[retryAttemptRef.current]!;
        retryAttemptRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          void load();
        }, delay);
      }
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, locationId]);

  useEffect(() => {
    setLoading(true);
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => {
      clearInterval(t);
      // Cancel any pending fast-retry from a prior failure so it doesn't
      // fire after unmount or after switching weeks (load identity changes
      // when fromDate/toDate change, which re-triggers this effect).
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      retryAttemptRef.current = 0;
    };
  }, [load]);

  const teamsByCell = useMemo(() => {
    const out = new Map<string, CalendarTeamSlot[]>();
    for (const t of teamSlots) {
      const k = `${t.date}|${t.time_of_day}`;
      const list = out.get(k);
      if (list) list.push(t);
      else out.set(k, [t]);
    }
    return out;
  }, [teamSlots]);

  // Pauses only for (location, date) pairs the calendar has slots for, so a
  // location that is closed or not bookable that day shows no pause.
  const breaksMap = useMemo(() => {
    const locs = new Set(departures.map((d) => d.location_id));
    const withSlots = new Set(departures.map((d) => `${d.location_id}|${d.date}`));
    const all = computeBreaks(scheduleRules, locs, fromDate, toDate)
      .filter((b) => withSlots.has(`${b.location_id}|${b.date}`));
    return breaksByCell(all);
  }, [scheduleRules, departures, fromDate, toDate]);

  const notesMap = useMemo(
    () => notesByCell(overrideRows, departures),
    [overrideRows, departures],
  );

  // "Alleen geboekt" keeps slots with bookings, plus slots whose override
  // carries a reason (planning info everyone should see).
  const visibleDepartures = useMemo(
    () =>
      onlyBooked
        ? departures.filter(
            (d) =>
              d.booked > 0 ||
              (notesMap.get(`${d.date}|${d.time_of_day}`) ?? []).some((n) => n.reason),
          )
        : departures,
    [departures, onlyBooked, notesMap],
  );

  const locationNames = useMemo(
    () => new Map(locations.map((l) => [l.id, l.display_name ?? l.name])),
    [locations],
  );

  // Per-day totals for the column headers. Sums booked + capacity across
  // every slot that day, including empty ones — gives the operator a
  // schedule-aware snapshot regardless of the "Alleen geboekt" filter.
  // `booked` is room-wide on every row (same value repeated for each
  // experience sharing a room/slot), so use `booked_in_experience` for the
  // per-row contribution to a people sum. Capacity dedupes per (slot, room).
  const perDayTotals = useMemo(() => {
    const out = new Map<string, { booked: number; capacity: number }>();
    const seenRoom = new Map<string, Set<string>>();
    for (const d of departures) {
      const acc = out.get(d.date) ?? { booked: 0, capacity: 0 };
      acc.booked += d.booked_in_experience;
      const roomKey = `${d.slot_start}|${d.room_id}`;
      let seen = seenRoom.get(d.date);
      if (!seen) { seen = new Set(); seenRoom.set(d.date, seen); }
      if (!seen.has(roomKey)) {
        seen.add(roomKey);
        acc.capacity += d.capacity;
      }
      out.set(d.date, acc);
    }
    return out;
  }, [departures]);

  // ---- Monthly people count (separate fetch from the visible week) -------
  // Bounds = first..last day of the month containing the anchor.
  const monthBounds = useMemo(() => {
    const [y, m] = anchor.split('-').map(Number);
    const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
    const mm = String(m).padStart(2, '0');
    return {
      first: `${y}-${mm}-01`,
      last:  `${y}-${mm}-${String(lastDay).padStart(2, '0')}`,
    };
  }, [anchor]);

  const [monthlyPeople, setMonthlyPeople] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchDepartures(monthBounds.first, monthBounds.last, { locationId });
        if (cancelled) return;
        setMonthlyPeople(rows.reduce((n, r) => n + r.booked_in_experience, 0));
      } catch {
        if (!cancelled) setMonthlyPeople(null);
      }
    })();
    return () => { cancelled = true; };
  }, [monthBounds.first, monthBounds.last, locationId, lastRefresh]);

  // ---- YTD people count (true year-to-date: Jan 1 -> today, venue tz) -------
  // Independent of the date picker. Server-side sum so a full year doesn't pull
  // the entire schedule grid like fetchDepartures would. CALENDAR_LIFECYCLE
  // (draft/confirmed/arrived) keeps it consistent with the week/month numbers
  // above. Headcount-only RPC on purpose — the analytics breakdown carries
  // revenue and is gated behind booking_view_analytics; the calendar is not.
  const [ytdPeople, setYtdPeople] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = todayVenue();
        const guests = await fetchYtdGuests(`${today.slice(0, 4)}-01-01`, today, {
          locationId,
          lifecycle: CALENDAR_LIFECYCLE,
        });
        if (cancelled) return;
        setYtdPeople(guests);
      } catch {
        if (!cancelled) setYtdPeople(null);
      }
    })();
    return () => { cancelled = true; };
  }, [locationId, lastRefresh]);

  // Group departures: time_of_day => date => Departure[].
  // (Multiple departures on the same day at the same time across rooms is
  // expected; the cell stacks them vertically.)
  const grid = useMemo(() => {
    const byTime = new Map<string, Map<string, Departure[]>>();
    for (const d of visibleDepartures) {
      let timeRow = byTime.get(d.time_of_day);
      if (!timeRow) {
        timeRow = new Map();
        byTime.set(d.time_of_day, timeRow);
      }
      const cell = timeRow.get(d.date) ?? [];
      cell.push(d);
      timeRow.set(d.date, cell);
    }
    // Pause rows: the schedule makes no slots there, so add the times.
    const inView = new Set(days);
    for (const key of breaksMap.keys()) {
      const [date, time] = key.split('|') as [string, string];
      if (!inView.has(date) || byTime.has(time)) continue;
      byTime.set(time, new Map());
    }
    return [...byTime.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [visibleDepartures, breaksMap, days]);

  const toggleOnlyBooked = useCallback(() => {
    setOnlyBooked((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(ONLY_BOOKED_KEY, next ? '1' : '0');
      } catch {
        /* ignore quota / private mode errors */
      }
      return next;
    });
  }, []);

  const goPrev = useCallback(
    () => setAnchor(addDaysVenue(anchor, mode === 'week' ? -7 : -1)),
    [anchor, mode],
  );
  const goNext = useCallback(
    () => setAnchor(addDaysVenue(anchor, mode === 'week' ? 7 : 1)),
    [anchor, mode],
  );
  const goToday = useCallback(() => setAnchor(todayVenue()), []);

  const todayStr = todayVenue();
  const headerLabel = mode === 'week' ? venueWeekLabel(fromDate) : formatVenueLong(anchor);
  const weekTotal = useMemo(
    // booked_in_experience: per-row contribution, safe to sum (each booking
    // belongs to exactly one experience). d.booked is room-wide and would
    // double-count across shared-room rows.
    () => departures.reduce((n, d) => n + d.booked_in_experience, 0),
    [departures],
  );
  const refreshedAt = useMemo(
    () =>
      lastRefresh
        ? lastRefresh.toLocaleTimeString('nl-NL', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        : null,
    [lastRefresh],
  );

  // "Now" line: tick every minute so it slides down through the grid as the
  // day progresses. Only renders when today is in the visible day range.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const nowTimeStr = useMemo(() => {
    void nowTick; // dependency for the minute ticker
    return new Date().toLocaleTimeString('en-GB', {
      timeZone: 'Europe/Amsterdam',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }, [nowTick]);
  const todayInView = days.includes(todayStr);
  // The "Nu" line sits INSIDE the row whose slot is currently running (the
  // last row whose time is <= now), positioned vertically at a percentage
  // proportional to how far we are between this row and the next one. If
  // now is before every visible row, anchor at the top of row 0. If now is
  // way past the last row (no anchor available), don't render the line.
  const nowAnchor: { time: string; topPct: number } | null = useMemo(() => {
    if (!todayInView || grid.length === 0) return null;
    const nowMin = parseHM(nowTimeStr);
    let runningIdx = -1;
    for (let i = 0; i < grid.length; i++) {
      if (parseHM(grid[i]![0]) <= nowMin) runningIdx = i;
      else break;
    }
    if (runningIdx === -1) {
      return { time: grid[0]![0], topPct: 0 };
    }
    const runningTime = grid[runningIdx]![0];
    const nextTime = grid[runningIdx + 1]?.[0];
    const denomMin = nextTime
      ? Math.max(1, parseHM(nextTime) - parseHM(runningTime))
      : 60;
    const elapsedMin = nowMin - parseHM(runningTime);
    // If we're more than a slot-length past the running row's start with no
    // following row to anchor to, the data ran out — skip the line entirely.
    if (elapsedMin > denomMin + 30) return null;
    const pct = Math.min(98, Math.max(0, (elapsedMin / denomMin) * 100));
    return { time: runningTime, topPct: pct };
  }, [grid, nowTimeStr, todayInView]);

  // Auto-scroll the grid to center on the "now" row whenever the visible
  // range first lands on today's window. Re-runs only when the anchor
  // changes — minute-ticks keep the same scroll position so the operator
  // doesn't get yanked while reading.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledAnchorRef = useRef<string | null>(null);

  // Pin the time column to the viewport's left edge as the calendar scrolls
  // horizontally. Native `position: sticky` on grid items has flaky behaviour
  // past certain scroll thresholds in Chrome, so we use scroll-driven CSS
  // animations (Chrome 115+) which run on the compositor in lockstep with
  // scroll. For browsers without that, fall back to a JS-driven transform via
  // --cal-scroll-x (functional but one frame behind the scroll).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const supportsScrollTimeline =
      typeof CSS !== 'undefined' &&
      typeof CSS.supports === 'function' &&
      CSS.supports('animation-timeline: scroll()');

    const updateMax = () => {
      el.style.setProperty(
        '--cal-scroll-max',
        `${Math.max(0, el.scrollWidth - el.clientWidth)}px`,
      );
    };
    updateMax();

    const ro = new ResizeObserver(updateMax);
    ro.observe(el);

    if (supportsScrollTimeline) {
      // Compositor handles tracking via the keyframe defined in CSS.
      return () => ro.disconnect();
    }

    let raf = 0;
    const updateX = () => {
      el.style.setProperty('--cal-scroll-x', `${el.scrollLeft}px`);
      raf = 0;
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(updateX);
    };
    updateX();
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [grid.length, days.length]);
  useEffect(() => {
    if (!todayInView || !nowAnchor) {
      lastScrolledAnchorRef.current = null;
      return;
    }
    if (lastScrolledAnchorRef.current === anchor) return;
    const scroller = scrollRef.current;
    if (!scroller || grid.length === 0) return;
    const raf = requestAnimationFrame(() => {
      const target = scroller.querySelector<HTMLElement>(
        `[data-row-time="${nowAnchor.time}"]`,
      );
      if (!target) return;
      const targetRect = target.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const offsetWithin =
        (targetRect.top - scrollerRect.top) + scroller.scrollTop;
      const center = offsetWithin - (scroller.clientHeight - target.clientHeight) / 2;
      scroller.scrollTop = Math.max(0, center);
      lastScrolledAnchorRef.current = anchor;
    });
    return () => cancelAnimationFrame(raf);
  }, [anchor, todayInView, nowAnchor, grid.length]);

  const openNewBooking = useCallback((prefill?: NewBookingPrefill) => {
    setNewBooking(prefill ?? {});
  }, []);

  // SlotCell click router: inspect the whole slot. DepartureModal lists
  // every booking across all experiences sharing this (date, time).
  const handleSlotSelect = (ds: Departure[]) => setSelected(ds);

  // SlotCell create router: the + button (or empty-slot body click) opens
  // NewBookingModal pre-filled. experienceId is omitted for multi-exp slots
  // so the operator picks inside the modal.
  const handleSlotCreate = (p: SlotCreatePrefill) => {
    openNewBooking({
      date:         p.date,
      time:         p.time,
      locationId:   p.locationId,
      experienceId: p.experienceId,
    });
  };

  const headerSlot = useMemo<HeaderSlot>(() => {
    const row1 = (
      <>
        <button className="btn-link cal-nav-btn" onClick={goPrev} aria-label="Vorige">‹</button>
        <div className="cal-title">{headerLabel}</div>
        <button className="btn-link cal-nav-btn" onClick={goNext} aria-label="Volgende">›</button>
        <button
          className="btn-link cal-today-btn cal-today-btn--desktop"
          onClick={goToday}
        >
          Vandaag
        </button>
        <div className="cal-mode cal-mode--desktop">
          <button
            className={`cal-mode-btn ${mode === 'day' ? 'cal-mode-active' : ''}`}
            onClick={() => setMode('day')}
          >
            Dag
          </button>
          <button
            className={`cal-mode-btn ${mode === 'week' ? 'cal-mode-active' : ''}`}
            onClick={() => setMode('week')}
          >
            Week
          </button>
        </div>
        <button
          className="cal-new-booking-btn cal-new-booking-btn--desktop"
          onClick={() => openNewBooking()}
        >
          + Nieuwe boeking
        </button>
      </>
    );

    const overflowPanel = (close: () => void) => (
      <div className="cal-overflow">
        <button
          type="button"
          className="cal-overflow-row"
          onClick={() => { goToday(); close(); }}
        >
          Vandaag
        </button>
        <label className="cal-overflow-row cal-overflow-row--field">
          <span className="cal-overflow-label">Datum</span>
          <input
            type="date"
            className="cal-date-input"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
          />
        </label>
        <div className="cal-overflow-row cal-overflow-row--field">
          <span className="cal-overflow-label">Weergave</span>
          <div className="cal-mode">
            <button
              className={`cal-mode-btn ${mode === 'day' ? 'cal-mode-active' : ''}`}
              onClick={() => setMode('day')}
            >
              Dag
            </button>
            <button
              className={`cal-mode-btn ${mode === 'week' ? 'cal-mode-active' : ''}`}
              onClick={() => setMode('week')}
            >
              Week
            </button>
          </div>
        </div>
        <label className="cal-overflow-row cal-overflow-row--check">
          <input
            type="checkbox"
            checked={onlyBooked}
            onChange={toggleOnlyBooked}
          />
          <span>Alleen geboekt</span>
        </label>
        <label className="cal-overflow-row cal-overflow-row--field">
          <span className="cal-overflow-label">Locatie</span>
          <select
            value={locationId ?? ''}
            onChange={(e) => setLocationId(e.target.value || null)}
            aria-label="Locatie"
          >
            <option value="">Alle locaties</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.display_name ?? l.name}
              </option>
            ))}
          </select>
        </label>
        <hr className="cal-overflow-sep" />
        <div className="cal-overflow-stat">
          <strong>{weekTotal}</strong>{' '}
          gasten {mode === 'day' ? 'op deze dag' : 'deze week'}
        </div>
        {monthlyPeople != null ? (
          <div className="cal-overflow-stat">
            <strong>{monthlyPeople}</strong> deze maand
          </div>
        ) : null}
        {refreshedAt ? (
          <div className="cal-overflow-stat muted">ververst {refreshedAt}</div>
        ) : null}
        {refreshError ? (
          <div className="cal-overflow-stat cal-meta-refresh-error" title={refreshError}>
            ververs mislukt
          </div>
        ) : null}
        <button
          type="button"
          className="cal-overflow-row"
          onClick={() => { void load(); close(); }}
        >
          ↻ Ververs
        </button>
      </div>
    );

    const desktopRow2 = (
      <div className="cal-meta">
        <input
          type="date"
          className="cal-date-input"
          value={anchor}
          onChange={(e) => e.target.value && setAnchor(e.target.value)}
        />
        <label className="cal-only-booked">
          <input
            type="checkbox"
            checked={onlyBooked}
            onChange={toggleOnlyBooked}
          />
          <span>Alleen geboekt</span>
        </label>
        <span>
          <strong>{weekTotal}</strong>
          {' '}gasten {mode === 'day' ? 'op deze dag' : 'deze week'}
        </span>
        {monthlyPeople != null ? (
          <>
            <span className="muted">·</span>
            <span>
              <strong>{monthlyPeople}</strong> deze maand
            </span>
          </>
        ) : null}
        {ytdPeople != null ? (
          <>
            <span className="muted">·</span>
            <span>
              <strong>{ytdPeople}</strong> YTD
            </span>
          </>
        ) : null}
        {refreshedAt ? (
          <span className="muted cal-meta-refresh">· ververst {refreshedAt}</span>
        ) : null}
        {refreshError ? (
          <span className="cal-meta-refresh-error" title={refreshError}>
            · ververs mislukt
          </span>
        ) : null}
        <button className="btn-link" onClick={() => void load()}>Ververs</button>
      </div>
    );

    const fab = (
      <button
        type="button"
        className="cal-fab"
        onClick={() => openNewBooking()}
        aria-label="Nieuwe boeking"
      >
        +
      </button>
    );

    return { row1, overflowPanel, desktopRow2, fab };
  }, [
    headerLabel,
    mode,
    anchor,
    onlyBooked,
    monthlyPeople,
    ytdPeople,
    weekTotal,
    refreshedAt,
    refreshError,
    locationId,
    locations,
    goPrev,
    goNext,
    goToday,
    openNewBooking,
    setLocationId,
    toggleOnlyBooked,
    load,
  ]);

  useHeaderSlot(headerSlot);

  return (
    <div className="screen calendar-screen">
      <div className="cal-scroll" ref={scrollRef}>
        {loading ? <div className="muted">Laden…</div> : null}
        {error ? <div className="error-card">{error}</div> : null}

        {!loading && !error && visibleDepartures.length === 0 ? (
          <div className="muted cal-empty">
            {onlyBooked && departures.length > 0
              ? 'Geen geboekte sessies in deze periode. Zet "Alleen geboekt" uit om alle sessies te zien.'
              : 'Geen sessies in deze periode.'}
          </div>
        ) : null}

        {grid.length > 0 ? (
          <div className={`cal-grid cal-grid-${mode}`}
            style={{ gridTemplateColumns: `var(--cal-time-col-w, 80px) repeat(${days.length}, minmax(140px, 1fr))` }}
          >
            <div className="cal-corner" />
            {days.map((d) => {
              const isToday = d === todayStr;
              const totals = perDayTotals.get(d);
              const pct =
                totals && totals.capacity > 0
                  ? Math.min(100, (totals.booked / totals.capacity) * 100)
                  : 0;
              return (
                <div key={d} className={`cal-day-head ${isToday ? 'cal-day-today' : ''}`}>
                  <div className="cal-day-head-label">{formatVenueShort(d)}</div>
                  {totals && totals.capacity > 0 ? (
                    <>
                      <div className="cal-day-head-totals">
                        {totals.booked} / {totals.capacity}
                      </div>
                      <div className="cal-day-head-bar" aria-hidden="true">
                        <div
                          className="cal-day-head-bar-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })}

            {grid.map(([time, byDate]) => (
              <ContextualRow
                key={time}
                time={time}
                days={days}
                byDate={byDate}
                onSelect={handleSlotSelect}
                onCreate={handleSlotCreate}
                onCreateEmpty={(date) => openNewBooking({ date, time })}
                nowLineForToday={nowAnchor && nowAnchor.time === time ? todayStr : null}
                nowLineTopPct={nowAnchor && nowAnchor.time === time ? nowAnchor.topPct : 0}
                nowTimeStr={nowTimeStr}
                onlyBooked={onlyBooked}
                teamsByCell={teamsByCell}
                breaksMap={breaksMap}
                notesMap={notesMap}
                locationNames={locationId ? null : locationNames}
              />
            ))}
          </div>
        ) : null}
      </div>

      {selected ? (
        <DepartureModal
          departures={selected}
          onClose={() => {
            setSelected(null);
            void load();
          }}
        />
      ) : null}

      {newBooking ? (
        <NewBookingModal
          prefillDate={newBooking.date}
          prefillTime={newBooking.time}
          prefillExperienceId={newBooking.experienceId}
          prefillLocationId={newBooking.locationId}
          onClose={(created) => {
            setNewBooking(null);
            if (created) void load();
          }}
        />
      ) : null}
    </div>
  );
}

function ContextualRow({
  time,
  days,
  byDate,
  onSelect,
  onCreate,
  onCreateEmpty,
  nowLineForToday,
  nowLineTopPct,
  nowTimeStr,
  onlyBooked,
  teamsByCell,
  breaksMap,
  notesMap,
  locationNames,
}: {
  time: string;
  days: string[];
  byDate: Map<string, Departure[]>;
  onSelect: (ds: Departure[]) => void;
  onCreate: (prefill: SlotCreatePrefill) => void;
  // Slot has no scheduled departures at all on this date — operator wants
  // to force-create off-schedule.
  onCreateEmpty: (date: string) => void;
  // YYYY-MM-DD of today when this row is the one the "Nu" line sits on
  // top of; null when the line doesn't belong on this row.
  nowLineForToday: string | null;
  // 0–98: vertical position WITHIN this cell, as a percentage of cell
  // height. 0 = at the top (slot just started); higher means more elapsed.
  nowLineTopPct: number;
  nowTimeStr: string;
  // Hide 0/x experience chips inside each slot — see SlotCell.
  onlyBooked: boolean;
  teamsByCell: Map<string, CalendarTeamSlot[]>;
  breaksMap: Map<string, CalendarBreak[]>;
  notesMap: Map<string, OverrideNote[]>;
  // Set when every location is in view: pause labels name the location.
  locationNames: Map<string, string> | null;
}) {
  return (
    <>
      <div className="cal-time-head" data-row-time={time}>
        <span className="cal-time-label">{time}</span>
      </div>
      {days.map((day) => {
        const cell = byDate.get(day) ?? [];
        const showNow = day === nowLineForToday;
        const empty = cell.length === 0;
        const teams = teamsByCell.get(`${day}|${time}`);
        const breaks = breaksMap.get(`${day}|${time}`);
        const notes = teams ? undefined : notesMap.get(`${day}|${time}`);
        // Every slot here is blocked (capacity 0) and no team holds it: the
        // regular cell would draw nothing, so draw a grey closed cell.
        const closed = !empty && !teams && cell.every((d) => d.capacity === 0);
        return (
          <div
            key={day}
            className={`cal-cell ${showNow ? 'cal-cell-now' : ''}`}
            data-row-time={time}
          >
            {showNow ? (
              <div
                className="cal-now-line"
                style={{ top: `${nowLineTopPct}%` }}
                aria-hidden="true"
              >
                <span className="cal-now-line-label">Nu · {nowTimeStr}</span>
              </div>
            ) : null}
            {empty && teams ? (
              <TeamSlotCell teams={teams} departures={[]} onSelect={onSelect} />
            ) : empty && breaks ? (
              <PauseCell breaks={breaks} locationNames={locationNames} />
            ) : closed ? (
              <ClosedCell departures={cell} notes={notes ?? []} onSelect={onSelect} />
            ) : empty ? (
              <button
                type="button"
                className="cal-cell-empty cal-cell-create"
                onClick={() => onCreateEmpty(day)}
                aria-label={`Nieuwe boeking om ${time} op ${day}`}
                title="Nieuwe boeking"
              >
                <span className="cal-cell-create-plus" aria-hidden="true">+</span>
              </button>
            ) : (
              <SlotCell
                departures={cell}
                onSelect={onSelect}
                onCreate={onCreate}
                onlyBooked={onlyBooked}
                teams={teams}
              />
            )}
            {!closed && notes
              ? notes.map((n, i) => {
                  const label = overrideNoteLabel(n, new Set(cell.map((d) => d.room_id)).size > 1);
                  return (
                    <div key={i} className="cal-note" title={label}>
                      {label}
                    </div>
                  );
                })
              : null}
            {!empty && breaks ? (
              <div className="cal-note cal-note-pause">
                Pauze{locationNames ? ` · ${breakLocations(breaks, locationNames)}` : ''}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

function breakLocations(breaks: CalendarBreak[], names: Map<string, string>): string {
  return [...new Set(breaks.map((b) => names.get(b.location_id) ?? ''))].filter(Boolean).join(', ');
}

// A pause between two opening periods: grey, not bookable.
function PauseCell({
  breaks,
  locationNames,
}: {
  breaks: CalendarBreak[];
  locationNames: Map<string, string> | null;
}) {
  const b = breaks[0]!;
  return (
    <div
      className="slot-cell slot-cell-pause"
      title={`Pauze ${b.start}–${b.end}: bewust vrij gehouden, niet boekbaar`}
    >
      <span className="slot-pause-label">Pauze</span>
      <span className="slot-pause-meta">
        {b.start}–{b.end}
        {locationNames ? ` · ${breakLocations(breaks, locationNames)}` : ''}
      </span>
    </div>
  );
}

// Every slot in the cell is closed by an override. Shows the reason; opens the
// slot's bookings when someone is still booked in it.
function ClosedCell({
  departures,
  notes,
  onSelect,
}: {
  departures: Departure[];
  notes: OverrideNote[];
  onSelect: (ds: Departure[]) => void;
}) {
  const booked = departures.some((d) => d.booked > 0);
  const reasons = [...new Set(notes.map((n) => n.reason).filter((r): r is string => !!r))];
  const label = reasons.length > 0 ? reasons.join(' · ') : null;
  const open = () => onSelect(departures);
  return (
    <div
      role={booked ? 'button' : undefined}
      tabIndex={booked ? 0 : undefined}
      className={`slot-cell slot-cell-closed ${booked ? '' : 'slot-cell-static'}`}
      title={label ? `Gesloten: ${label}` : 'Gesloten (override)'}
      onClick={booked ? open : undefined}
      onKeyDown={
        booked
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                open();
              }
            }
          : undefined
      }
    >
      <span className="slot-pause-label">Gesloten</span>
      {label ? <span className="slot-closed-reason">{label}</span> : null}
      {booked ? (
        <>
          <div className="slot-chips">
            {departures
              .filter((d) => d.booked_in_experience > 0)
              .map((d) => (
                <span key={d.room_id + d.experience_id} className="slot-chip slot-chip-closed">
                  <span className="slot-chip-name">{d.experience_name}</span>
                  <span className="slot-chip-count">{d.booked_in_experience}</span>
                </span>
              ))}
          </div>
          <span className="slot-pause-meta">{bookedPeople(departures)} geboekt · dicht gezet</span>
        </>
      ) : null}
    </div>
  );
}

// People booked across the cell's rooms (`booked` is room-wide, so once per room).
function bookedPeople(departures: Departure[]): number {
  const byRoom = new Map<string, number>();
  for (const d of departures) byRoom.set(d.room_id, d.booked);
  return [...byRoom.values()].reduce((n, b) => n + b, 0);
}
